import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UserStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import debounce from 'utils/debounce';

const USERS_QUERY = gql(`
  query fetchUsersForApproval(
    $cursor: ID
    $limit: Int
    $filters: UsersFilterType
  ) {
    users(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        fullName
        email
        status
        createdAt
      }
      pageInfo {
        hasNextPage
        cursor
      }
    }
  }
`);

const ApproveRuvUsersPage: FC = () => {
  const navigate = useNavigate();

  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    USERS_QUERY,
    {
      variables: {
        limit: 50,
        filters: {
          status: UserStatusEnumType.RuvReview,
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const users = data.users.nodes;
  const pageInfo = data.users.pageInfo;

  return (
    <>
      <TextField
        placeholder="Search users"
        onChange={debounce(e => {
          refetch({
            filters: {
              ...variables?.filters,
              text: e.target.value,
            },
          });
        }, 500)}
      />
      <DataTable
        data={users}
        onClick={user => navigate(`/users/${user.id}`)}
        columns={[
          {
            label: 'Name',
            fieldName: 'fullName',
          },
          {
            label: 'Email',
            fieldName: 'email',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
          {
            label: 'Closing Date',
            fieldName: 'closingDate',
            type: 'DATE',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATE',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        filterLoading={networkStatus === NetworkStatus.setVariables}
        paginationLoading={loading}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />
    </>
  );
};

export default ApproveRuvUsersPage;
